'use client';

import { c as _c } from "react/compiler-runtime";
import type { AutoFixLoopStatusView } from '@/lib/auto-fix-loop/status';
import { useLiveQuery } from '@tanstack/react-db';
import { focusManager, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { autoFixLoopStatusQueryKey, getAutoFixLoopCollection } from './auto-fix-loop-collection';
import { isAutoFixPlanLimit } from '@/lib/auto-fix-allowance/model';
export { autoFixLoopStatusQueryKey } from './auto-fix-loop-collection';

/**
 * While the plan's limit pauses the loop, the organization can get room back where nothing pushes
 * to this PR, such as an upgrade in the pricing tab the pause opened. Coming back to the tab reads
 * the status again, so Resume shows without a reload.
 */
function useRefetchPlanLimitPauseOnFocus(owner, repo, number, data) {
  const $ = _c(9);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== data) {
    t0 = data?.status === "paused" && data.pause !== null && isAutoFixPlanLimit(data.pause);
    $[0] = data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const planLimitPaused = t0;
  let t1;
  let t2;
  if ($[2] !== number || $[3] !== owner || $[4] !== planLimitPaused || $[5] !== queryClient || $[6] !== repo) {
    t1 = () => {
      if (!planLimitPaused) {
        return;
      }
      return focusManager.subscribe(focused => {
        if (focused) {
          queryClient.invalidateQueries({
            queryKey: autoFixLoopStatusQueryKey(owner, repo, number)
          }, {
            cancelRefetch: false
          });
        }
      });
    };
    t2 = [planLimitPaused, queryClient, owner, repo, number];
    $[2] = number;
    $[3] = owner;
    $[4] = planLimitPaused;
    $[5] = queryClient;
    $[6] = repo;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
}

/**
 * Read-only loop status for the PR page. Status changes are pushed over the
 * PR v2 realtime channel (`auto_fix` domain), which invalidates this query
 * key — no polling. The default focus refetch is the safety net if the
 * socket drops.
 */
export function useAutoFixLoopStatus(owner, repo, number, t0) {
  const $ = _c(15);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[2] !== number || $[3] !== options.enabled || $[4] !== owner || $[5] !== queryClient || $[6] !== repo) {
    t2 = (options.enabled ?? true) && owner && repo && number ? getAutoFixLoopCollection(queryClient, owner, repo, number) : null;
    $[2] = number;
    $[3] = options.enabled;
    $[4] = owner;
    $[5] = queryClient;
    $[6] = repo;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const collection = t2;
  let t3;
  let t4;
  if ($[8] !== collection) {
    t3 = () => collection;
    t4 = [collection];
    $[8] = collection;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  const live = useLiveQuery(t3, t4);
  const data = live.data?.[0];
  useRefetchPlanLimitPauseOnFocus(owner, repo, number, data);
  const t5 = data === undefined ? collection?.utils.lastError ?? null : null;
  let t6;
  if ($[11] !== data || $[12] !== live.isLoading || $[13] !== t5) {
    t6 = {
      data,
      isPending: live.isLoading,
      error: t5
    };
    $[11] = data;
    $[12] = live.isLoading;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}